<template>
  <div class="purchases-page">
    <div class="page-header">
      <h1>Purchase History</h1>
      <div class="sort-control">
        <label for="sortOrder">Sort by: </label>
        <select id="sortOrder" v-model="sortOrder" class="sort-select">
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
        </select>
      </div>
    </div>
    <div v-if="loading" class="loading">
      Loading orders...
    </div>
    <div v-else-if="error" class="error-message">
      {{ error }}
    </div>
    <div v-else class="orders-container">
      <div v-if="orders.length === 0" class="no-orders">
        <p>No orders found</p>
        <router-link to="/" class="shop-now">Shop Now</router-link>
      </div>
      <div v-else class="order-list">
        <div v-for="(order, index) in paginatedOrders" :key="order.id" class="order-card" :data-order-id="order.id">
          <div class="order-header">
            <div class="order-info">
              <div class="order-header-top">
                <h3>Order #{{ order.id }}</h3>
                <button @click="printOrder(order.id)" class="print-button-single">
                  <i class="fas fa-print"></i> Print Order
                </button>
              </div>
              <p class="order-date">{{ formatDate(order.created_at) }}</p>
              <p class="order-status" :class="order.status">Status: {{ order.status }}</p>
              <div class="customer-info">
                <p><strong>Name:</strong> {{ order.customer_name || 'N/A' }}</p>
                <p><strong>Username:</strong> {{ order.customer_username || 'N/A' }}</p>
                <p><strong>Email:</strong> {{ order.customer_email || 'N/A' }}</p>
              </div>
            </div>
            <div class="order-total">
              <div class="subtotal">Subtotal: €{{ (order.total_amount - order.shipping_cost).toFixed(2) }}</div>
              <div class="shipping">Shipping: €{{ order.shipping_cost.toFixed(2) }}</div>
              <div class="total">Total: €{{ order.total_amount.toFixed(2) }}</div>
            </div>
          </div>
          <div class="shipping-address">
            <p><strong>Shipping Address:</strong> {{ order.street_address }}{{ order.apt_suite ? `, ${order.apt_suite}` : '' }}, {{ order.city }}, {{ order.state }} {{ order.postal_code }}, {{ order.country }}{{ order.phone ? ` • Phone: ${order.phone}` : '' }}</p>
          </div>
          <div class="order-items">
            <div v-for="item in order.items" :key="item.id" class="order-item">
              <div class="item-details">
                <h4>
                  <a :href="`/product?id=${item.product_id}&categoryId=${item.category_id}`" target="_blank" class="product-link">
                    {{ item.product_name }}
                  </a>
                </h4>
                <p class="item-price">€{{ item.price.toFixed(2) }} x {{ item.quantity }}</p>
              </div>
              <div class="item-total">
                €{{ (item.price * item.quantity).toFixed(2) }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Add pagination controls -->
      <div v-if="totalPages > 1" class="pagination">
        <button 
          :disabled="currentPage === 1" 
          @click="currentPage--" 
          class="pagination-button"
        >
          Previous
        </button>
        <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
        <button 
          :disabled="currentPage === totalPages" 
          @click="currentPage++" 
          class="pagination-button"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '../config';

export default {
  name: 'Purchases',
  data() {
    return {
      orders: [],
      loading: false,
      error: null,
      isAdmin: false,
      sortOrder: 'newest',
      currentPage: 1,
      ordersPerPage: 5
    };
  },
  computed: {
    sortedOrders() {
      return [...this.orders].sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return this.sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
      });
    },
    totalPages() {
      return Math.ceil(this.sortedOrders.length / this.ordersPerPage);
    },
    paginatedOrders() {
      const start = (this.currentPage - 1) * this.ordersPerPage;
      const end = start + this.ordersPerPage;
      return this.sortedOrders.slice(start, end);
    }
  },
  watch: {
    sortOrder() {
      // Reset to first page when sorting changes
      this.currentPage = 1;
    }
  },
  methods: {
    formatDate(dateString) {
      const options = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return new Date(dateString).toLocaleDateString('en-US', options);
    },
    printOrders() {
      window.print();
    },
    printOrder(orderId) {
      const orderElement = document.querySelector(`[data-order-id="${orderId}"]`);
      if (orderElement) {
        const printContent = orderElement.innerHTML;
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
          <html>
            <head>
              <title>Order #${orderId}</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 20px; }
                .order-card { border: 1px solid #ddd; padding: 20px; }
                .order-header { margin-bottom: 20px; }
                .order-status.completed { color: #4CAF50; }
                .order-status.pending { color: #f57c00; }
                .customer-info { margin: 10px 0; }
                .shipping-address { margin: 10px 0; padding: 10px 0; border-top: 1px solid #eee; }
                .order-items { margin-top: 20px; }
                .order-item { padding: 10px 0; border-bottom: 1px solid #eee; }
                .order-total { margin-top: 20px; text-align: right; }
                .print-button-single { display: none; }
                @media print {
                  body { padding: 0; }
                  .print-button-single { display: none; }
                }
              </style>
            </head>
            <body>
              <div class="order-card">${printContent}</div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      }
    },
    async checkUserRole() {
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}/api/auth/check-session`,
          { withCredentials: true }
        );
        this.isAdmin = response.data.user?.role === 'admin';
        return this.isAdmin;
      } catch (error) {
        console.error('Error checking user role:', error);
        return false;
      }
    },
    async fetchOrders() {
      this.loading = true;
      try {
        // Check if user is admin first
        const isAdmin = await this.checkUserRole();
        
        // Use different endpoint for admin
        const endpoint = isAdmin 
          ? `${config.apiBaseUrl}/api/orders/all`
          : `${config.apiBaseUrl}/api/orders/user`;

        const response = await axios.get(endpoint, { 
          withCredentials: true,
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        // Handle the response data
        let ordersData = response.data;
        if (typeof ordersData === 'object' && !Array.isArray(ordersData)) {
          // Remove _cachedAt property if it exists
          const { _cachedAt, ...ordersObj } = ordersData;
          ordersData = Object.values(ordersObj);
        }
        
        this.orders = ordersData.map(order => ({
          ...order,
          shipping_cost: parseFloat(order.shipping_cost) || 0,
          total_amount: parseFloat(order.total_amount) || 0
        }));
      } catch (error) {
        console.error('Error fetching orders:', error);
        this.error = 'Failed to load orders. Please try again.';
      } finally {
        this.loading = false;
      }
    }
  },
  async created() {
    await this.fetchOrders();
  }
};
</script>

<style scoped>
.purchases-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.print-button {
  padding: 0.5rem 1rem;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.print-button:hover {
  background: #45a049;
  transform: translateY(-1px);
}

.print-button i {
  font-size: 1rem;
}

.order-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 0.5rem;
  gap: 1rem;
}

.order-header-top h3 {
  margin: 0;
  padding-right: 1rem;
  flex: 1;
}

.print-button-single {
  padding: 0.3rem 0.8rem;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  transition: all 0.3s ease;
  white-space: nowrap;
  min-width: fit-content;
}

.print-button-single:hover {
  background: #45a049;
  transform: translateY(-1px);
}

.print-button-single i {
  font-size: 0.9rem;
}

/* Print-specific styles */
@media print {
  .page-header,
  .print-button-single,
  .order-card:not(:only-child) {
    display: none;
  }

  .order-card:only-child {
    break-inside: avoid;
    page-break-inside: avoid;
    box-shadow: none;
    border: 1px solid #ddd;
    margin: 0;
  }

  .purchases-page {
    padding: 0;
  }

  .order-card {
    break-inside: avoid;
    page-break-inside: avoid;
    box-shadow: none;
    border: 1px solid #ddd;
    margin-bottom: 1rem;
  }

  .order-header,
  .shipping-address {
    background: none;
    border-bottom: 1px solid #ddd;
  }

  .product-link {
    color: #333;
    text-decoration: none;
  }

  .shop-now {
    display: none;
  }

  @page {
    margin: 1cm;
  }
}

h1 {
  margin-bottom: 1.5rem;
  color: #333;
  font-size: 1.5rem;
}

.loading, .error-message {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.error-message {
  color: #dc3545;
}

.no-orders {
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.shop-now {
  display: inline-block;
  padding: 0.8rem 1.5rem;
  background: #4CAF50;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  margin-top: 1rem;
  transition: all 0.3s ease;
}

.shop-now:hover {
  opacity: 0.9;
  transform: translateY(-2px);
}

.order-card {
  background: white;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  margin-bottom: 1rem;
  overflow: hidden;
}

.order-header {
  padding: 1rem;
  background: #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 1px solid #eee;
}

.order-info h3 {
  margin: 0;
  color: #333;
  font-size: 1rem;
}

.order-date {
  color: #666;
  font-size: 0.8rem;
  margin: 0.3rem 0;
}

.order-status {
  font-size: 0.8rem;
  font-weight: 500;
  margin: 0;
}

.customer-info {
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: #666;
}

.customer-info p {
  margin: 0.2rem 0;
  line-height: 1.4;
}

.customer-info strong {
  color: #333;
  font-weight: 600;
  margin-right: 0.3rem;
}

.order-status.pending {
  color: #f57c00;
}

.order-status.completed {
  color: #4CAF50;
}

.shipping-address {
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  font-size: 0.9rem;
}

.shipping-address p {
  margin: 0;
  color: #666;
  font-size: 0.85rem;
  line-height: 1.4;
}

.shipping-address strong {
  color: #333;
  font-weight: 600;
  margin-right: 0.3rem;
}

.order-total {
  text-align: right;
  font-size: 0.85rem;
}

.order-total .subtotal,
.order-total .shipping {
  color: #666;
  margin-bottom: 0.2rem;
}

.order-total .total {
  font-weight: bold;
  font-size: 1rem;
  margin-top: 0.3rem;
  padding-top: 0.3rem;
  border-top: 1px solid #eee;
}

.order-items {
  padding: 0.75rem 1rem;
}

.order-item {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #eee;
  align-items: center;
}

.order-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.item-details {
  min-width: 0; /* Allows text truncation to work */
}

.item-details h4 {
  margin: 0;
  color: #333;
  font-size: 0.9rem;
  line-height: 1.4;
}

.item-details h4 a {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-price {
  color: #666;
  margin: 0.3rem 0 0;
  font-size: 0.85rem;
}

.item-total {
  font-weight: 500;
  color: #333;
  font-size: 0.9rem;
  white-space: nowrap;
}

.product-link {
  color: #2196F3;
  text-decoration: none;
  transition: color 0.2s ease;
}

.product-link:hover {
  color: #1976D2;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .purchases-page {
    padding: 0.75rem;
  }

  .order-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .order-total {
    margin-top: 0.75rem;
    width: 100%;
  }

  .order-item {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .item-total {
    text-align: left;
  }

  .item-details h4 {
    font-size: 0.85rem;
  }
}

.sort-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sort-select {
  padding: 0.4rem 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  font-size: 0.9rem;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;
}

.sort-select:hover {
  border-color: #4CAF50;
}

.sort-select:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1);
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .sort-control {
    width: 100%;
  }
  
  .sort-select {
    flex: 1;
  }
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1rem;
}

.pagination-button {
  padding: 0.5rem 1rem;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.pagination-button:hover:not(:disabled) {
  background: #45a049;
  transform: translateY(-1px);
}

.pagination-button:disabled {
  background: #cccccc;
  cursor: not-allowed;
  transform: none;
}

.page-info {
  font-size: 0.9rem;
  color: #666;
}

@media (max-width: 768px) {
  .pagination {
    flex-direction: row;
    gap: 0.5rem;
  }
  
  .pagination-button {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
}
</style> 